import { useRef } from 'react';
import { Textarea } from '../../components/ui/textarea';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { cn } from '../../lib/utils';
import { captionCounts, type CaptionCount } from '../../lib/publishing/characterLimits';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import type { WildcardContext } from '../../lib/publishing/captionWildcards';
import type { WildcardInsertMode } from '../../lib/publishing/wildcardInsert';
import { EmojiPicker } from './EmojiPicker';
import { WildcardMenu } from './WildcardMenu';

// Publishing composer §B — the caption box: the base caption, an emoji picker on
// the toolbar, and a LIVE per-platform character counter row (channel logo +
// remaining count), one entry per ticked destination.
//
// The counters are ADVISORY and read local constants (characterLimits.ts) — a
// per-keystroke round-trip to Zernio isn't possible. Zernio's own
// validatePost/validatePostLength remains the pre-publish authority and still
// runs on the debounce, feeding the preview warnings.

export function CaptionBox({
  value,
  selected,
  effectiveCaptionFor,
  wildcardMode,
  wildcardContext,
  wildcardAccountId,
  disabled,
  onChange,
}: {
  value: string;
  selected: PostDestinationId[];
  effectiveCaptionFor: (id: PostDestinationId) => string;
  // REQUIRED, so both composers have to declare which authoring model they use.
  // 'resolved' (single account) inserts the account's real text; 'token' (multi)
  // inserts the {token} for per-account resolution at dispatch — see
  // src/lib/publishing/wildcardInsert.ts.
  wildcardMode: WildcardInsertMode;
  wildcardContext: WildcardContext | null;
  wildcardAccountId?: string | null;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const counts = captionCounts(selected, effectiveCaptionFor);

  // Insert at the caret rather than appending — an AM adding an emoji mid-caption
  // shouldn't have it land at the end.
  function insert(emoji: string) {
    const el = ref.current;
    if (!el) {
      onChange(value + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    onChange(value.slice(0, start) + emoji + value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">Caption</label>
        <div className="flex items-center gap-1">
          {/* Both insert at the caret via the same `insert` helper. */}
          <WildcardMenu
            mode={wildcardMode}
            context={wildcardContext}
            accountId={wildcardAccountId}
            onInsert={insert}
            disabled={disabled}
          />
          <EmojiPicker onPick={insert} disabled={disabled} />
        </div>
      </div>
      <Textarea
        ref={ref}
        value={value}
        disabled={disabled}
        rows={6}
        placeholder="Write a caption with text, links…"
        onChange={(e) => onChange(e.target.value)}
      />
      {counts.length > 0 && <CounterRow counts={counts} />}
    </div>
  );
}

// One compact chip per ticked destination: channel logo + the remaining count.
// A destination whose surface publishes NO caption (stories) shows "no caption"
// instead of a number — see characterLimits.ts.
function CounterRow({ counts }: { counts: CaptionCount[] }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {counts.map((c) => (
        <span
          key={c.destination.id}
          className={cn('inline-flex items-center gap-1', c.over ? 'text-red-600' : 'text-slate-500')}
          title={
            c.limit === null
              ? `${c.destination.label} — this surface publishes no caption`
              : `${c.destination.label} — ${c.count} of ${c.limit} characters`
          }
        >
          <ChannelIcon channel={c.destination.platform} className="h-3.5 w-3.5" />
          {c.destination.placement === 'story' && (
            <span className="rounded bg-slate-100 px-1 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
              Story
            </span>
          )}
          {c.limit === null ? (
            <span className="italic">no caption</span>
          ) : (
            <span className={cn('tabular-nums', c.over && 'font-semibold')}>{c.remaining}</span>
          )}
        </span>
      ))}
    </div>
  );
}
